import { FC, useLayoutEffect, useRef, useState } from "react";
import styles from "./CollectionCard.module.scss";
import arrowIcon from "../../icons/arrow.png";
import puzzleIcon from "./icons/puzzle.png";
import collection2Image from "./icons/collection2.jpg";
import collection3Image from "./icons/collection3.jpg";
import collection4Image from "./icons/collection4.jpg";
import collection5Image from "./icons/collection5.jpg";
import fragmentIcon from "../../icons/fragment.png";
import { IUserCollection, PrizeType } from "../../redux/collections/get-collections.handler";
import { Ticket, TicketType } from "../TicketsPanel/Ticket";

interface IItem {
    icon: string;
    alt: string;
    count: number;
}

const sprites: Record<number, string> = {
    2: collection2Image,
    3: collection3Image,
    4: collection4Image,
    5: collection5Image,
}

const HEIGHT = 70;
const COLUMNS = 4;
const ROWS = 3;
const COLLECTION_ITEMS_COUNT = COLUMNS * ROWS;
const DEFAULT_ALT = 'no-item';

export const CollectionCard: FC<IUserCollection> = ({ collectionId, name, items, type }) => {
    const [backgroundImage, setBackgroundImage] = useState('');
    
    const [progress, setProgress] = useState(0);
    const [spriteWidth, setSpriteWidth] = useState(0);
    const [backgroundSize, setBackgroundSize] = useState('');

    const [itemsMap, setItemsMap] = useState<IItem[]>([]);
    const spriteRef = useRef<HTMLDivElement | null>(null);

    const handleImageLoad = () => {
        if (spriteRef.current) {
            const width = spriteRef.current.offsetWidth;
            setSpriteWidth(width);
            setBackgroundSize(`${width * COLUMNS}px ${HEIGHT * ROWS}px`);
        }
    };

    useLayoutEffect(() => {
        const img = new Image();
        img.src = sprites[collectionId];
        img.onload = handleImageLoad;

        setBackgroundImage(sprites[collectionId]);
    }, [collectionId]);

    useLayoutEffect(() => {
        const newItemsMap = new Array(12).fill(null);

        items.forEach((item, index) => {
            newItemsMap[item.partNumber - 1] = {
                icon: puzzleIcon,
                alt: `item${index}`,
                count: item.count
            };
        });

        setItemsMap(newItemsMap);
    }, [items]);

    useLayoutEffect(() => {
        setProgress(Math.round(items.length / COLLECTION_ITEMS_COUNT * 100));
    }, [items.length]);

    const getBackgroundPosition = (index: number) => {
        const w = (index % COLUMNS) * spriteWidth;
        const h = Math.floor(index / COLUMNS) * HEIGHT;
        return `${-w}px ${-h}px`;
    }

    const customPrize = (icon: string) => {
        return (
            <>
                <img className={ styles.customPrizeImage} src={ icon } alt='prize' />
                <span className={ styles.customPrizeAmount}>1</span>
            </>
        );
    }

    const getPrize = () => {
        const getTicketType = () => {
                switch (type) {
                    case PrizeType.RareTicket: return TicketType.Rare;
                    case PrizeType.EpicTicket: return TicketType.Epic;
                    case PrizeType.LegendaryTicket: return TicketType.Legendary;
                }
            }

        const ticketType = getTicketType();

        if (ticketType)
            return <Ticket type={ ticketType } value={1} precission={0}/>

        switch (type) {
            case PrizeType.Fragment: return customPrize(fragmentIcon);
        }
    }

    return (
        <div className={styles.collectionCard}>
            <div className={styles.header}>
                <h3 className={styles.title}>{name}</h3>
            </div>

            <div className={styles.progressSection}>
                <div className={styles.progressHeader}>
                    <span className={styles.progressLabel}>
                        Выполнено
                        <img
                            src={arrowIcon}
                            alt="Arrow Icon"
                            className={styles.arrowIcon}
                        />
                    </span>

                    <span className={styles.progressValue}>{progress}%</span>
                </div>

                <div className={styles.progressBar}>
                    <div
                        className={styles.progressFill}
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>

            <div className={styles.items}>
                {itemsMap.map((item, index) => (
                    item !== null ? 
                        <div
                            key={`collectionPart${index}`}
                            ref={spriteRef}
                            style={{
                                backgroundImage: `url(${backgroundImage})`,
                                backgroundPosition: getBackgroundPosition(index),
                                backgroundSize: backgroundSize
                            }}
                            className={styles.item}
                        >
                            <span className={styles.itemCount}>
                                {item.count}
                            </span>
                        </div> :
                        <div
                            key={index}
                            className={styles.itemEmpty}
                        >
                            <img src={puzzleIcon} alt={DEFAULT_ALT} />
                        </div>
                ))}
            </div>

            <div
                className={styles.collectionReward}
            >
                <span>{`Собрав коллекцию вы получите`}</span>
                <span className={styles.rowFlex}>{getPrize()}</span>
            </div>
        </div>
    );
};
